/**
 * "Connect Asana from Gyro" (docs/DESIGN-asana-connect-from-gyro.md sections 1, 2, 5 and 6), the widget's data half: the
 * `connect` field a tool row may carry, the words of the Connect control and of every refusal, the poll's bounds, and
 * the look-alike rule for links in the model's text. No DOM here, so every rule is testable as plain data.
 *
 * The control is driven by a structured field (`{ family, capability }`), never by a URL and never by the model's text,
 * and it renders on the desktop app only: the Office pane and the web hosts have no Bridge Asana path, so they show the
 * sentence alone, with no button and no sentence pointing at the desktop (section 6). The widget never receives an
 * authorize URL: the desktop's native layer consumes it; an answer that carries one anyway is refused here.
 */
export const PERSON_CONNECT_FAMILIES = ['asana', 'dropbox', 'bluebeam'];
const CAPABILITIES = ['read', 'write'];
/** The vendor's name as a person reads it on the control ("Connect Bluebeam Studio", never "Bluebeam" alone: Revu is local). */
export const PERSON_CONNECT_VENDOR = { asana: 'Asana', dropbox: 'Dropbox', bluebeam: 'Bluebeam Studio' };
/**
 * The `connect` field of a tool event, exactly `{ family, capability }` with known values; anything else is nothing.
 * Dropbox has one tier, so a Dropbox `write` is nothing too (the Bridge's GyroPersonConnectAction, gyro-core toolConnectOf).
 */
export function parsePersonConnect(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const keys = Object.keys(value);
    if (keys.length !== 2 || !keys.includes('family') || !keys.includes('capability'))
        return undefined;
    const { family, capability } = value;
    if (!PERSON_CONNECT_FAMILIES.includes(family) || !CAPABILITIES.includes(capability))
        return undefined;
    if (family === 'dropbox' && capability !== 'read')
        return undefined;
    return { family: family, capability: capability };
}
/**
 * Whether Gyro's own access on a connection (the Bridge's `gyroAccess`: none, read or read_and_write) covers a tier:
 * read needs read or read_and_write, write needs read_and_write (gyro-svc bridgeMe.ts gyroAccessCovers).
 */
export function personGyroAccessCovers(gyroAccess, capability) {
    return capability === 'write' ? gyroAccess === 'read_and_write' : gyroAccess === 'read' || gyroAccess === 'read_and_write';
}
/** The control's label: "Connect Asana"; a write tier reads "Allow changes in Asana" so the two tiers are told apart. */
export function personConnectLabel(connect) {
    const vendor = PERSON_CONNECT_VENDOR[connect.family];
    return connect.capability === 'write' ? `Allow changes in ${vendor}` : `Connect ${vendor}`;
}
const BLOCKED_BY = ['suspended', 'not_configured', 'no_tier'];
const WORD = /^[a-z][a-z0-9_]{0,39}$/;
/**
 * `{ connections: { asana?, dropbox?, bluebeam? } }` as gyro-svc answers it: exactly the five fields per vendor, plus
 * `connectBlockedBy` while Connect cannot start (one of the three known words; any other value, or one beside
 * `canConnect: true`, is left out); nothing else travels.
 */
export function parsePersonConnections(body) {
    const root = body && typeof body === 'object' ? body.connections : undefined;
    if (!root || typeof root !== 'object' || Array.isArray(root))
        return undefined;
    const out = {};
    for (const family of PERSON_CONNECT_FAMILIES) {
        const entry = root[family];
        if (!entry || typeof entry !== 'object' || Array.isArray(entry))
            continue;
        const { state, gyroAccess, canConnect, capability, pending, connectBlockedBy } = entry;
        if (typeof state !== 'string' || !WORD.test(state))
            continue;
        const blockedBy = canConnect !== true && BLOCKED_BY.includes(connectBlockedBy) ? connectBlockedBy : undefined;
        out[family] = {
            state,
            gyroAccess: typeof gyroAccess === 'string' && WORD.test(gyroAccess) ? gyroAccess : 'none',
            canConnect: canConnect === true,
            ...(capability === 'read' || capability === 'write' ? { capability } : {}),
            pending: pending === true,
            ...(blockedBy ? { connectBlockedBy: blockedBy } : {}),
        };
    }
    return out;
}
/**
 * The sentence the Connectors row shows in place of Connect when the Bridge says why it cannot start (#610; what the
 * person can do, from the-bridge#1572's table), in plain words with the vendor's name and never a code. Undefined for no
 * reason or an unknown one: the row then keeps its bare line (personConnectionStatus's suspended or setup).
 */
export function personConnectBlockedText(blockedBy, family) {
    switch (blockedBy) {
        case 'suspended': return 'An administrator paused this connection. Ask IT to turn it back on.';
        case 'not_configured': return "IT hasn't finished setting this up yet.";
        case 'no_tier': return `IT hasn't approved Gyro to connect ${PERSON_CONNECT_VENDOR[family]} yet.`;
        default: return undefined;
    }
}
/**
 * The Connectors row's status word for a vendor's connection state. `connected` means Gyro can use it: the person's
 * connection is connected AND Gyro's own access is not `none`. A connection the person told Gyro to stop using (revoke
 * keeps the connection and removes only Gyro's authorizations) reads as not connected, with Connect when the Bridge says
 * Connect can start (its healthy-connection shortcut then records Gyro's authorization again), never as a connected row
 * whose "Stop Gyro using" could only answer nothing_to_remove.
 */
export function personConnectionStatus(connection) {
    if (!connection)
        return 'setup';
    if (connection.state === 'suspended')
        return 'suspended';
    if (connection.state === 'connected' && connection.gyroAccess !== 'none')
        return 'connected';
    return connection.canConnect ? 'not-connected' : 'setup';
}
export const PERSON_CONNECT_POLL_FLOOR_MS = 5_000;
export const PERSON_CONNECT_POLL_CEILING_MS = 30_000;
/** After this long in a flow the poll backs off to this interval (section 3, the cost of a poll). */
export const PERSON_CONNECT_BACKOFF_AFTER_MS = 60_000;
export const PERSON_CONNECT_BACKOFF_MS = 15_000;
export const PERSON_CONNECT_FLOW_MAX_MS = 10 * 60_000;
/** 12 ticks in the first minute at 5 s, then 36 at 15 s for the other nine: never more per Connect press. */
export const PERSON_CONNECT_MAX_TICKS = 48;
const CODE = /^[a-z][a-z0-9_]{0,60}$/;
/**
 * The desktop's native answer to a connect-start, checked: `connected`, `authorize` (expiresAt bounded at ten minutes,
 * pollAfterMs clamped), or an error by code. An answer that carries `authorizeUrl` at all is refused as a contract fault:
 * the renderer must never hold the URL, whichever layer let it through.
 */
export function parsePersonConnectStart(status, body, now = Date.now()) {
    const json = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
    if (Object.hasOwn(json, 'authorizeUrl'))
        return { kind: 'error', code: 'bridge_contract' };
    const family = PERSON_CONNECT_FAMILIES.includes(json.family) ? json.family : undefined;
    if (status === 200 && json.status === 'connected' && family) {
        return { kind: 'connected', family, ...(typeof json.gyroAccess === 'string' && WORD.test(json.gyroAccess) ? { gyroAccess: json.gyroAccess } : {}) };
    }
    if (status === 200 && json.status === 'authorize' && family) {
        const at = typeof json.expiresAt === 'string' ? Date.parse(json.expiresAt) : Number.NaN;
        const expiresAtMs = Number.isFinite(at) && at > now ? Math.min(at, now + PERSON_CONNECT_FLOW_MAX_MS) : now + PERSON_CONNECT_FLOW_MAX_MS;
        const poll = typeof json.pollAfterMs === 'number' && Number.isFinite(json.pollAfterMs) ? json.pollAfterMs : PERSON_CONNECT_POLL_FLOOR_MS;
        return { kind: 'authorize', family, expiresAtMs, pollAfterMs: Math.min(PERSON_CONNECT_POLL_CEILING_MS, Math.max(PERSON_CONNECT_POLL_FLOOR_MS, Math.round(poll))), reused: json.reused === true };
    }
    const code = typeof json.error === 'string' && CODE.test(json.error) ? json.error : status === 401 ? 'signin_required' : 'failed';
    const retryAfterMs = typeof json.retryAfter === 'number' && json.retryAfter > 0 ? json.retryAfter * 1000 : undefined;
    return { kind: 'error', code, ...(retryAfterMs ? { retryAfterMs } : {}) };
}
// ─── The poll's bounds ────────────────────────────────────────────────────────
/** The wait before the next tick: the server's interval within 5–30 s, and at least 15 s once the flow is a minute old. */
export function personConnectPollDelayMs(flowStartedAtMs, nowMs, pollAfterMs) {
    const base = Math.min(PERSON_CONNECT_POLL_CEILING_MS, Math.max(PERSON_CONNECT_POLL_FLOOR_MS, pollAfterMs));
    return nowMs - flowStartedAtMs >= PERSON_CONNECT_BACKOFF_AFTER_MS ? Math.max(base, PERSON_CONNECT_BACKOFF_MS) : base;
}
/**
 * Why the poll stops, or undefined to keep going: the family is connected with Gyro's access at the tier the flow asked
 * for (`capability`, read when absent: a write flow on a connection Gyro already reads keeps polling until the write
 * tier lands, since the connection's `state` is `connected` from the first tick), the Bridge says no flow of the
 * person's is open any more (declined, or ended some other way), the flow expired, or the tick budget is spent.
 */
export function personConnectPollStop(input) {
    const connection = input.connections?.[input.family];
    if (connection?.state === 'connected' && personGyroAccessCovers(connection.gyroAccess, input.capability ?? 'read'))
        return 'connected';
    if (input.nowMs >= input.expiresAtMs)
        return 'expired';
    if (input.ticks >= PERSON_CONNECT_MAX_TICKS)
        return 'budget';
    if (connection && !connection.pending)
        return 'ended';
    return undefined;
}
// ─── Refusals, in the words the person sees (section 5) ───────────────────────
/**
 * The codes that mean connecting is not switched on, for this PC or at the Bridge: gyro-svc's own (the person receiver
 * not registered or not offered) and the Bridge's contract codes (the family not configured, the receiver switched off,
 * the person pair not armed). The Connectors rows stay hidden after them (usePersonConnect) and the button goes.
 */
export const PERSON_CONNECT_NOT_SWITCHED_ON = new Set(['person_receiver_unavailable', 'not_configured', 'receiver_disabled', 'assertion_unavailable']);
/** Codes after which the button goes: pressing it again could never work until IT changes something. */
const HIDES_BUTTON = new Set([...PERSON_CONNECT_NOT_SWITCHED_ON, 'connection_suspended', 'custody_ambiguous']);
export function personConnectButtonHidden(code) {
    return code !== undefined && HIDES_BUTTON.has(code);
}
/**
 * The fixed sentence under the control for a refusal code; never the server's text. The codes are gyro-svc's own and
 * the Bridge's contract codes gyro-svc relays (connect-start, revoke and disconnect on the person receiver). `action`
 * picks the sentence for a code no other line names: `connect` (the Connect control) or `change` (Stop Gyro using,
 * Disconnect).
 */
export function personConnectRefusalText(code, family, action = 'connect') {
    const v = PERSON_CONNECT_VENDOR[family];
    switch (code) {
        case 'gyro_person_token_required': return `Gyro on this PC needs an update before it can connect ${v}.`;
        case 'person_token_invalid':
        case 'person_token_subject_mismatch': return 'Sign in to Gyro on this PC again, then press Connect.';
        case 'signin_required':
        case 'entra_sign_in_required': return `Gyro needs you to sign in again before it can connect ${v}.`;
        case 'person_receiver_unavailable':
        case 'not_configured':
        case 'receiver_disabled':
        case 'assertion_unavailable': return `Connecting ${v} is not switched on yet.`;
        case 'bridge_unavailable':
        case 'desktop_forward_timeout':
        case 'desktop_forward_failed': return 'The Bridge is not reachable right now. Try again in a minute.';
        case 'custody_unavailable':
        case 'custody_busy':
        case 'replay_defense_unavailable':
        case 'person_token_unavailable':
        case 'deadline_exceeded': return 'The Bridge could not finish this just now. Try again in a few minutes.';
        case 'bridge_contract': return 'Something went wrong between Gyro and the Bridge. Tell IT.';
        case 'connection_suspended': return `Your ${v} connection was suspended. Ask IT.`;
        case 'custody_ambiguous': return `Something is wrong with your ${v} connection at the Bridge. Ask IT.`;
        case 'rate_limited': return 'You already have a connection in progress. Finish it in your browser, or wait ten minutes.';
        case 'app_grant_required': return `Gyro is not approved to connect ${v} at that level yet. Ask IT.`;
        case 'authorize_url_rejected': return `Something was wrong with the ${v} sign-in link, so Gyro did not open it. Try again; if it keeps happening, tell IT.`;
        case 'browser_launch_failed': return 'Gyro could not open your browser. Try again.';
        case 'cancelled': return 'Nothing was changed.';
        default: return action === 'change' ? `Gyro could not change your ${v} connection. Try again.` : `Gyro could not start connecting ${v}. Try again.`;
    }
}
/** The words of the control while a flow runs or after it ends. */
export function personConnectPhaseText(phase, family) {
    const v = PERSON_CONNECT_VENDOR[family];
    return phase === 'starting' ? 'Opening your browser…' : phase === 'waiting' ? `Waiting for ${v}…` : `${v} connected`;
}
// ─── The look-alike rule (section 6) ──────────────────────────────────────────
const BRIDGE_HOST = 'bridge.exante360.com';
/** The vendors' OAuth authorize endpoints (gyro-svc personConnect.ts PERSON_CONNECT_PINS, the desktop's PersonConnectPins): host and path, exactly. */
const VENDOR_OAUTH_ENDPOINTS = [
    { host: 'app.asana.com', path: '/-/oauth_authorize' },
    { host: 'www.dropbox.com', path: '/oauth2/authorize' },
    { host: 'api.bluebeam.com', path: '/oauth2/authorize' },
];
const CONNECT_WORDS = /\b(connect|sign[ -]?in|log[ -]?in|authori[sz]e)\b/i;
/** Whether `host` is the Bridge's or under it. */
function bridgeHost(host) {
    return host === BRIDGE_HOST || host.endsWith(`.${BRIDGE_HOST}`);
}
/** Whether `host` is Asana's, Dropbox's or Bluebeam's, or under any of them. */
function vendorHost(host) {
    return host === 'asana.com' || host.endsWith('.asana.com') || host === 'dropbox.com' || host.endsWith('.dropbox.com') || host === 'bluebeam.com' || host.endsWith('.bluebeam.com');
}
/**
 * A link in the model's text that must render as plain text with its host shown, never as a link, because it could stand
 * in for Gyro's own Connect control: a vendor's OAuth authorize endpoint (`app.asana.com/-/oauth_authorize`,
 * `www.dropbox.com/oauth2/authorize`), the Bridge's connect pages (its host, or one under it, under `/connect/`), or a
 * link whose text says connect, sign in, log in or authorise AND whose host is a vendor's or the Bridge's. Answers the host
 * to show, or undefined when the link may render as one. An ordinary Asana task permalink or Dropbox shared link is a
 * link; so is "Connect Asana" pointing at an unrelated site, which is only a page. There is no Bridge-origin exception.
 */
export function lookAlikeLinkHost(href, text) {
    let host;
    let path;
    try {
        const url = new URL(href);
        if (url.protocol === 'mailto:')
            return undefined;
        host = url.hostname.toLowerCase();
        path = url.pathname.toLowerCase();
    }
    catch {
        return undefined;
    }
    const oauthEndpoint = VENDOR_OAUTH_ENDPOINTS.some((endpoint) => endpoint.host === host && endpoint.path === path);
    const bridgeConnect = bridgeHost(host) && (path === '/connect' || path.startsWith('/connect/'));
    const connectWords = CONNECT_WORDS.test(text) && (vendorHost(host) || bridgeHost(host));
    if (oauthEndpoint || bridgeConnect || connectWords)
        return host;
    return undefined;
}
//# sourceMappingURL=personConnect.js.map